import { useLayoutEffect, useRef } from 'react';
import './PageIntroBand.css';

// Shared public-page introduction: one short statement, two explanations and
// the About watermark styling shared by every matching page.
export default function PageIntroBand({ testId, label, title, backgroundText, paragraphs }) {
  const sectionRef = useRef(null);
  const watermarkRef = useRef(null);
  useLayoutEffect(() => {
    const section = sectionRef.current;
    const watermark = watermarkRef.current;
    const context = document.createElement('canvas').getContext('2d');
    if (!section || !watermark || !context) return undefined;
    let active = true;
    const align = () => {
      if (!active) return;
      const style = getComputedStyle(watermark);
      const size = parseFloat(style.fontSize);
      context.font = `${style.fontStyle} ${style.fontWeight} ${style.fontSize} ${style.fontFamily}`;
      const metrics = context.measureText(backgroundText);
      const ascent = metrics.fontBoundingBoxAscent ?? size * .9;
      const descent = metrics.fontBoundingBoxDescent ?? size * .2;
      const baseline = (parseFloat(style.lineHeight) - ascent - descent) / 2 + ascent;
      const top = watermark.offsetTop + baseline - metrics.actualBoundingBoxAscent;
      const bottom = watermark.offsetTop + baseline + metrics.actualBoundingBoxDescent;
      section.style.setProperty('--intro-letter-center', `${(top + bottom) / 2}px`);
      section.style.setProperty('--intro-letter-top', `${top}px`);
      section.style.setProperty('--intro-letter-bottom', `${bottom}px`);
    };
    const observer = new ResizeObserver(align);
    observer.observe(watermark);
    align();
    document.fonts?.ready.then(align);
    return () => { active = false; observer.disconnect(); };
  }, [backgroundText]);

  return (
    <section ref={sectionRef} data-testid={testId} className="page-intro-band">
      <div className="page-intro-band__glow" aria-hidden="true" />
      <span ref={watermarkRef} className="page-intro-band__watermark pointer-events-none absolute left-[-5vw] top-6 select-none font-display text-[22vw] leading-none tracking-tighter text-ink/[0.035]" aria-hidden="true">{backgroundText}</span>
      <div className="page-intro-band__content">
        <div className="page-intro-band__statement">
          <p className="page-intro-band__label">{label}</p>
          <h2>{title}</h2>
        </div>
        <div className="page-intro-band__explanation">
          {paragraphs.map(paragraph => <p key={paragraph}>{paragraph}</p>)}
        </div>
      </div>
    </section>
  );
}
